import { expeditionsDeprecated, expeditionsOrder } from "."
import { useCallback, useEffect, useMemo, useState } from "react"
import { TAGS } from "store/api"
import { useAppDispatch } from "store/index"
import { recipeApi } from "store/apis/recipeAPI"
import { runesApi } from "store/apis/runesAPI/runes.api"
import { setErrorMessage } from "store/slices/commonSlice"
import { ExpeditionRewardsCurrency } from "store/apis/expeditionsAPI/types"
import {
  getAllExpeditions,
  getAttempts,
  useFinishExpeditionMutation,
  useGetExpeditionRewardsMutation,
  useStartExpeditionMutation,
  useGetAllExpeditionsQuery,
  useGetExpeditionProgressQuery
} from "store/apis/expeditionsAPI"
import { useModal } from "context/ModalsContext"
import { ServerDate } from "helpers/server-date.class.ts"
import { calculateRemainingTime } from "helpers/calculateRemainingTime"
import { IUseExpeditionsResult } from "./types"

const useExpeditions = (): IUseExpeditionsResult => {
  const dispatch = useAppDispatch()
  const { openModal, closeModal } = useModal()

  const [timeLeft, setTimeLeft] = useState<number | null>(null)
  const [isExpeditionFinished, setIsExpeditionFinished] =
    useState<boolean>(false)

  const { data: expeditionsList, isLoading: isExpeditionListLoading } =
    useGetAllExpeditionsQuery(undefined)
  const { data: currExpedition, isFetching: isCurrExpeditionFetching } =
    useGetExpeditionProgressQuery()

  const [
    startExpedition,
    { isLoading: isStarting, error: startExpeditionErr }
  ] = useStartExpeditionMutation()
  const [
    claimRewards,
    { isLoading: isClaiming, error: claimRewardsExpeditionErr }
  ] = useGetExpeditionRewardsMutation()
  const [
    finishExpedition,
    { isLoading: isFinishing, error: finishExpeditionErr }
  ] = useFinishExpeditionMutation()

  useEffect(() => {
    if (
      startExpeditionErr ||
      claimRewardsExpeditionErr ||
      finishExpeditionErr
    ) {
      openModal({ type: "globalError" })
      dispatch(
        setErrorMessage(
          startExpeditionErr || claimRewardsExpeditionErr || finishExpeditionErr
        )
      )
    }
  }, [
    startExpeditionErr,
    claimRewardsExpeditionErr,
    finishExpeditionErr,
    currExpedition,
    expeditionsList
  ])

  const currExpeditionInfo = currExpedition
    ? expeditionsList?.find((e) => e._id === currExpedition.chosenExpedition)
    : null

  const reward = currExpedition?.reward
    ? (Object.entries(currExpedition.reward) as [
        ExpeditionRewardsCurrency,
        number
      ][])
    : null

  const runeReward = currExpedition?.runeReward || null
  const recipeReward = currExpedition?.recipeReward || null

  const isExpeditionStarted = currExpedition?.status === "inProgress"

  const isExpeditionChosen = useCallback(
    (id: string) => currExpedition?.chosenExpedition === id,
    [currExpedition]
  )

  const getTimeLeftById = useCallback(
    (id: string): number | null => (isExpeditionChosen(id) ? timeLeft : null),
    [isExpeditionChosen, timeLeft]
  )

  const initAllExpeditionList = async () => {
    await dispatch(getAllExpeditions.initiate())
  }

  const recoverAttempts = async () => {
    await dispatch(getAttempts.initiate(undefined, { forceRefetch: true }))
  }

  const handleStart = async (
    id: string,
    onStarted?: () => void,
    onError?: () => void
  ) => {
    try {
      const result = await startExpedition(id)
      if (result.error) {
        onError?.()
      } else {
        onStarted?.()
      }
    } catch {
      onError?.()
    }
  }

  const handleClaim = async () => {
    try {
      if (currExpedition?.reward) {
        openModal({ type: "expeditionFinished" })
      } else {
        openModal({ type: "pageLoading" })
        await claimRewards()
        openModal({ type: "expeditionFinished" })
        closeModal("pageLoading")
      }
    } catch (error) {
      console.error("Error claiming rewards:", error)
    }
  }

  const handleFinish = async (onError?: () => void) => {
    try {
      const result = await finishExpedition()

      if (runeReward?.length) {
        dispatch(runesApi.util.invalidateTags([TAGS.RUNES]))
      }

      if (recipeReward?.length) {
        dispatch(recipeApi.util.invalidateTags([TAGS.RECIPES]))
      }

      if (result.error) {
        onError?.()
      }
    } catch {
      onError?.()
    }
  }

  const updateTimeLeft = useCallback(() => {
    if (currExpedition?.status === "inProgress" && currExpedition.finishAt) {
      const finishDate = new ServerDate(currExpedition.finishAt).getTime()
      const remaining = calculateRemainingTime(finishDate)

      setIsExpeditionFinished(remaining <= 1000)
      setTimeLeft(remaining)
    }
  }, [currExpedition])

  useEffect(() => {
    if (currExpedition?.status === "inProgress" && currExpedition.finishAt) {
      const timer = setInterval(updateTimeLeft, 1000)
      updateTimeLeft()
      return () => clearInterval(timer)
    } else {
      setTimeLeft(null)
      setIsExpeditionFinished(false)
    }
  }, [currExpedition, updateTimeLeft])

  const filteredExpeditionsList = useMemo(() => {
    return (expeditionsList || [])
      .filter((e) => !expeditionsDeprecated.includes(e.title))
      .sort((a, b) => expeditionsOrder[a.title] - expeditionsOrder[b.title])
  }, [expeditionsList])

  return {
    isExpeditionListLoading: !!isExpeditionListLoading,
    expeditionsList: filteredExpeditionsList,
    currExpedition,
    isCurrExpeditionFetching,
    currExpeditionInfo,
    isExpeditionStarted,
    isExpeditionFinished,
    timeLeft,
    reward,
    runeReward: runeReward || null,
    recipeReward: recipeReward || null,
    isExpeditionChosen,
    getTimeLeftById,
    isStarting,
    isClaiming,
    isFinishing,
    start: handleStart,
    claim: handleClaim,
    finish: handleFinish,
    recoverAttempts,
    initAllExpeditionList
  }
}

export default useExpeditions
